import React, { useEffect, useMemo, useState } from "react";
import { getSalesEntries } from "../storage";
import { formatDisplayDate, toComparableYmd } from "../utils/dateFormat";
import "../StockInward.css";
import { getSettlementSummary } from "../utils/settlement";

export function billingCustomerName(entry) {
  return String(entry.customerName || "").trim() ||
    String(entry.customer || "").split(/\r?\n/)[0].trim() || "Walk-in";
}

export default function BillingSummaryReport() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [customer, setCustomer] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    let active = true;
    getSalesEntries().then((data) => {
      if (active) setEntries(Array.isArray(data) ? data : []);
    }).catch((err) => {
      if (active) setError(err.message || "Unable to load summary report.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const invalidRange = from && to && from > to;
  const rows = useMemo(() => entries.filter((entry) => {
    const date = toComparableYmd(entry.saleDate);
    return entry.category === "Billing" && (!from || date >= from) && (!to || date <= to) &&
      billingCustomerName(entry).toLowerCase().includes(customer.trim().toLowerCase());
  }).map((entry) => ({ ...entry, settlement: getSettlementSummary(entry.total, entry.payments) })).sort((a, b) => String(b.saleDate).localeCompare(String(a.saleDate))), [entries, from, to, customer]);
  const total = rows.reduce((sum, row) => sum + Math.round((Number(row.total) || 0) * 100), 0) / 100;
  const paidTotal = rows.reduce((sum, row) => sum + Math.round(row.settlement.paidAmount * 100), 0) / 100;
  const pendingTotal = rows.reduce((sum, row) => sum + Math.round(row.settlement.pendingAmount * 100), 0) / 100;
  const pages = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, pages);
  const visible = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const paymentStatus = (entry) => entry.paymentDone === true || Number(entry.paymentDone) === 1 ? "Done" : "Not Done";

  const exportCsv = () => {
    const values = [["Date", "Customer Name", "Sales", "Paid Amount", "Pending Amount", "Payment Status", "Payment Mode"], ...rows.map((entry) => [
      formatDisplayDate(entry.saleDate), billingCustomerName(entry), Number(entry.total || 0).toFixed(2),
      entry.settlement.paidAmount.toFixed(2), entry.settlement.pendingAmount.toFixed(2),
      paymentStatus(entry), entry.paymentType || "Any",
    ])];
    const csv = values.map((row) => row.map((value) => {
      const text = String(value);
      const safe = /^[=+@\-\t\r]/.test(text) ? "'" + text : text;
      return '"' + safe.replace(/"/g, '""') + '"';
    }).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "sales-summary.csv";
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return <div className="stock-inward-container">
    <h2>Sales Summary Report</h2>
    <p>Sales and payment details.</p>
    <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "end", marginBottom: 20 }}>
      <label>From Date<input className="input-field" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} /></label>
      <label>To Date<input className="input-field" type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} /></label>
      <label>Customer Name<input className="input-field" value={customer} placeholder="Search customer name" onChange={(e) => { setCustomer(e.target.value); setPage(1); }} /></label>
      <button className="btn-secondary" onClick={() => { setFrom(""); setTo(""); setCustomer(""); setPage(1); }}>Reset</button>
      <button className="btn-primary" disabled={loading || !!error || !rows.length || !!invalidRange} onClick={exportCsv}>Export CSV</button>
    </div>
    {invalidRange ? <p role="alert">From Date must be on or before To Date.</p> : null}
    {error ? <p role="alert">{error}</p> : loading ? <p role="status">Loading report...</p> : <>
      <p><strong>Sales: {total.toFixed(2)}</strong> &middot; <strong>Paid Amount: {paidTotal.toFixed(2)}</strong> &middot; <strong>Pending Amount: {pendingTotal.toFixed(2)}</strong> &middot; {rows.length} bills</p>
      <div className="entries-table-container" style={{ overflowX: "auto" }}>
        <table className="entries-table" style={{ minWidth: 820, tableLayout: "auto" }}>
          <thead><tr><th>Date</th><th>Customer Name</th><th className="text-right">Sales</th><th className="text-right">Paid Amount</th><th className="text-right">Pending Amount</th><th>Payment Status</th><th>Payment Mode</th></tr></thead>
          <tbody>{visible.length ? visible.map((entry) => <tr key={entry.id}>
            <td>{formatDisplayDate(entry.saleDate)}</td><td>{billingCustomerName(entry)}</td>
            <td className="text-right">{Number(entry.total || 0).toFixed(2)}</td>
            <td className="text-right">{entry.settlement.paidAmount.toFixed(2)}</td>
            <td className="text-right">{entry.settlement.pendingAmount.toFixed(2)}</td>
            <td>{paymentStatus(entry)}</td><td>{entry.paymentType || "Any"}</td>
          </tr>) : <tr><td colSpan={7}>No bills match these filters.</td></tr>}</tbody>
        </table>
      </div>
      <div role="navigation" aria-label="Report pagination" style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginTop: 16 }}>
        <label>Rows per page <select className="input-field compact" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
          {[10, 25, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
        </select></label>
        <span aria-live="polite">Showing {rows.length ? (currentPage - 1) * pageSize + 1 : 0} to {Math.min(currentPage * pageSize, rows.length)} of {rows.length} bills</span>
        <button className="btn-secondary" disabled={currentPage === 1} onClick={() => setPage(1)}>First</button>
        <button className="btn-secondary" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>Previous</button>
        <span>Page {currentPage} of {pages}</span>
        <button className="btn-secondary" disabled={currentPage === pages} onClick={() => setPage(currentPage + 1)}>Next</button>
        <button className="btn-secondary" disabled={currentPage === pages} onClick={() => setPage(pages)}>Last</button>
      </div>
    </>}
  </div>;
}
